import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../LoadingPanel";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { getCSRFToken } from '../../app/getCSRFToken';
import { useDisableNumberInputScroll } from '../../app/useDisableNumberInputScroll';
import { Button } from "@progress/kendo-react-buttons";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { CommandCell } from '../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { filterBy } from "@progress/kendo-data-query";

export const AdministratorMinimumAgePOSNotes = (props) => {
    //Use the custom hook to disable scrolling in number inputs
    useDisableNumberInputScroll();

    const dispatch = useDispatch();

    const editField = "inEdit";

    const [minimumAgePOSNotes, setMinimumAgePOSNotes] = useState([]);
    const [areMinimumAgePOSNotesLoading, setAreMinimumAgePOSNotesLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [noteDescriptions, setNoteDescriptions] = useState([]);
    const [age, setAge] = useState(undefined);

    const [autocompleteState, setAutocompleteState] = useState({
        data: [],
        value: "",
    });

    const getMinimumAgePOSNotes = () => {
        setAreMinimumAgePOSNotesLoading(true);

        fetch(`/api/administrator/minimumageposnotes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setAreMinimumAgePOSNotesLoading(false);
                setMinimumAgePOSNotes(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreMinimumAgePOSNotesLoading(false);
            });
    }

    const deleteMinimumAgePOSNote = (minimumAgePOSNoteKey) => {
        const dataItem = {
            minimumAgePOSNoteKey: minimumAgePOSNoteKey
        }

        fetch("/api/administrator/minimumageposnote",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getMinimumAgePOSNotes();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const saveMinimumAgePOSNote = () => {
        if (!age) {
            addNotificationWithTimeout(dispatch, "error", "You must enter an age");
            return;
        }

        const note = notes.find(element => element.description === autocompleteState.value);

        if (!note) {
            addNotificationWithTimeout(dispatch, "error", "You must select a note");
            return;
        }

        const dataItem = {
            minimumAgeKey: props.minimumAgeKey,
            age: age,
            posNoteId: note.id,
        }

        fetch("/api/administrator/minimumageposnote",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Created item successfully");

                getMinimumAgePOSNotes();

                let newAutocompleteState = autocompleteState;
                newAutocompleteState.value = "";
                setAutocompleteState(newAutocompleteState);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const discard = (dataItem) => {
        const newMinimumAgePOSNotes = [...minimumAgePOSNotes];
        newMinimumAgePOSNotes.splice(0, 1);
        setMinimumAgePOSNotes(newMinimumAgePOSNotes);
    };

    const remove = (dataItem) => {
        deleteMinimumAgePOSNote(dataItem.minimumAgePOSNoteKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="minimumAgePOSNoteKey"
        />
    );

    const itemChange = (event) => {
        const field = event.field || "";
        const newMinimumAgePOSNotes = minimumAgePOSNotes.map((item) =>
            item.minimumAgePOSNoteKey === event.dataItem.minimumAgePOSNoteKey
                ? { ...item, [field]: event.value }
                : item
        );
        setMinimumAgePOSNotes(newMinimumAgePOSNotes);
    };

    const getPOSNotes = () => {
        fetch(`/api/administrator/pos/additionalnotes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setNotes(result);

                let localNoteDescriptions = [];
                result.forEach(element => localNoteDescriptions.push(element.description));

                setNoteDescriptions(localNoteDescriptions);
                setAutocompleteState({
                    data: localNoteDescriptions,
                    value: "",
                });
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const filterNotes = (value) => {
        const data = noteDescriptions;

        const filter = {
            value: value,
            operator: "contains",
            ignoreCase: true,
        };       

        return value ? filterBy(data, filter) : data;
    };

    const noteChanged = (event) => {
        const value = event.value;

        setAutocompleteState({
            data: filterNotes(value),
            value: value,
        });
    };

    const ageChanged = (event) => {
        setAge(event.value);
    };

    useEffect(() => {
        document.title = "Minimum Age POS Notes";

        getPOSNotes();
        getMinimumAgePOSNotes();
    }, []);

    return (
        <div className="p-3">
            <div className="mb-3">
                <Label>Add note:</Label> 

                <div className="row">
                    <div className="col-md-6">
                        <NumericTextBox
                            placeholder="Minimum age"
                            value={age}
                            onChange={ageChanged}
                        />
                    </div>
                    <div className="col-md-6">
                        <AutoComplete
                            id={"note"}
                            name={"note"}
                            data={autocompleteState.data}   
                            value={autocompleteState.value}
                            placeholder="Note description"
                            onChange={noteChanged}
                        />
                    </div>
                </div>
                
                <Button
                    themeColor={"primary"}
                    type={"button"}
                    onClick={saveMinimumAgePOSNote}
                    className="mt-2"
                    >
                    Save
                </Button>
            </div>

            <Grid
                data={minimumAgePOSNotes}
                onItemChange={itemChange}
                editField={editField}
                dataItemKey={"minimumAgePOSNoteKey"}
                resizable={true}
                scrollable="none"
            >
                <Column field="age" title={"Minimum age"} />
                <Column field="description" title={"Note"} />
                <Column cell={MyCommandCell} width="150px" />
            </Grid>

            {areMinimumAgePOSNotesLoading ? <LoadingPanel /> : <></>}
        </div>
    );
}